import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import MaterialTable, { Column } from "material-table";
import BreadcrumbNav from "../../../../components/BreadcrumbNav/BreadcrumbNav";
import CustomButton from "../../../../components/Button/CustomButton";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { materialTableES } from "../../../../services/materialTableES";
import { errorToast, successToast } from "../../../../services/toasts";
import { RbacPermission, RbacRole } from "../../../../types/rbac";
import styles from "./RolesManagement.module.scss";
import {
  useCreateRbacRole,
  useDeleteRbacRole,
  useGetRbacPermissions,
  useGetRbacRoles,
  useSetRbacRolePermissions,
  useUpdateRbacRole,
} from "../../../../utils/react-query/queryRbac/queries";

type RoleFormState = {
  name: string;
  description: string;
  permission_ids: number[];
};

const INITIAL_ROLE_FORM: RoleFormState = {
  name: "",
  description: "",
  permission_ids: [],
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  return error instanceof Error ? error.message : fallbackMessage;
};

const RolesManagement = () => {
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [roleForm, setRoleForm] = useState<RoleFormState>(INITIAL_ROLE_FORM);
  const [selectedRole, setSelectedRole] = useState<RbacRole | null>(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const { data: roles = [], isLoading: isLoadingRoles } = useGetRbacRoles();
  const { data: permissions = [], isLoading: isLoadingPermissions } =
    useGetRbacPermissions();
  const { mutateAsync: createRole, isLoading: isCreatingRole } =
    useCreateRbacRole();
  const { mutateAsync: updateRole, isLoading: isUpdatingRole } =
    useUpdateRbacRole();
  const { mutateAsync: deleteRole } = useDeleteRbacRole();
  const { mutateAsync: setRolePermissions, isLoading: isSavingPermissions } =
    useSetRbacRolePermissions();

  const summary = useMemo(() => {
    const systemRoles = roles.filter((role) => role.is_system).length;

    return {
      totalRoles: roles.length,
      systemRoles,
      customRoles: roles.length - systemRoles,
    };
  }, [roles]);

  const permissionsByModule = useMemo<Record<string, RbacPermission[]>>(() => {
    return permissions.reduce<Record<string, RbacPermission[]>>(
      (acc, permission) => {
        if (!acc[permission.module]) {
          acc[permission.module] = [];
        }

        acc[permission.module].push(permission);
        return acc;
      },
      {}
    );
  }, [permissions]);

  const openCreateDialog = () => {
    setSelectedRole(null);
    setRoleForm(INITIAL_ROLE_FORM);
    setIsRoleDialogOpen(true);
  };

  const openEditDialog = (role: RbacRole) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || "",
      permission_ids: role.permissions.map((permission) => permission.id),
    });
    setIsRoleDialogOpen(true);
  };

  const closeRoleDialog = () => {
    setSelectedRole(null);
    setRoleForm(INITIAL_ROLE_FORM);
    setIsRoleDialogOpen(false);
  };

  const togglePermission = (permissionId: number) => {
    setRoleForm((prev) => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter((currentPermissionId) => {
            return currentPermissionId !== permissionId;
          })
        : [...prev.permission_ids, permissionId],
    }));
  };

  const handleSaveRole = async () => {
    if (!selectedRole && !roleForm.name.trim()) {
      return errorToast("El nombre del rol es obligatorio");
    }

    try {
      let roleId = selectedRole?.id;

      if (selectedRole) {
        await updateRole({
          roleId: selectedRole.id,
          data: {
            name: roleForm.name.trim(),
            description: roleForm.description.trim(),
          },
        });
      } else {
        const createdRole = await createRole({
          name: roleForm.name.trim(),
          description: roleForm.description.trim(),
        });
        roleId = createdRole.id;
      }

      if (roleId) {
        await setRolePermissions({
          roleId,
          permission_ids: roleForm.permission_ids,
        });
      }

      successToast(
        selectedRole ? "Rol actualizado con éxito" : "Rol creado con éxito"
      );
      closeRoleDialog();
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible guardar el rol"));
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      await deleteRole(roleId);
      successToast("Rol eliminado con éxito");
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible eliminar el rol"));
    }
  };

  const columns: Column<RbacRole>[] = [
    {
      title: "Rol",
      field: "name",
      render: (rowData) => (
        <div>
          <Typography className={styles.roleName}>{rowData.name}</Typography>
          <Typography className={styles.secondaryText}>
            {rowData.description || "Sin descripción"}
          </Typography>
        </div>
      ),
    },
    {
      title: "Tipo",
      field: "is_system",
      filtering: false,
      render: (rowData) => (
        <Chip
          size="small"
          className={rowData.is_system ? styles.systemChip : styles.customChip}
          label={rowData.is_system ? "Sistema" : "Personalizado"}
        />
      ),
    },
    {
      title: "Permisos",
      field: "permissions",
      filtering: false,
      render: (rowData) => `${rowData.permissions?.length || 0} permisos`,
    },
    {
      title: "Acciones",
      field: "actions",
      filtering: false,
      sorting: false,
      render: (rowData) => (
        <Box display="flex" alignItems="center">
          <Tooltip title="Editar rol">
            <IconButton onClick={() => openEditDialog(rowData)}>
              <EditIcon className={styles.editIcon} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              rowData.is_system
                ? "Los roles del sistema no se pueden eliminar"
                : "Eliminar rol"
            }
          >
            <span>
              <IconButton
                disabled={rowData.is_system}
                onClick={() =>
                  setConfirmDialog({
                    isOpen: true,
                    title: "Eliminar rol",
                    subTitle:
                      "Solo se eliminará el rol personalizado. ¿Deseas continuar?",
                    onConfirm: async () => {
                      await handleDeleteRole(rowData.id);
                      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                    },
                  })
                }
              >
                <DeleteIcon
                  className={
                    rowData.is_system
                      ? styles.deleteIconDisabled
                      : styles.deleteIcon
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <section className="layout-dashboard-container">
      <div className="header-container">
        <Box className={styles.headerRow}>
          <div>
            <h4 className="header-title">Roles y permisos</h4>
            <BreadcrumbNav
              firstText="Proveedor"
              secondText="Roles y permisos"
              secondLink="/proveedor/roles-permisos"
              thirdText="Gestión"
            />
          </div>
        </Box>
      </div>

      <DeleteConfirmationDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <Grid container spacing={2} className={styles.summaryGrid}>
        {[
          { label: "Total roles", value: summary.totalRoles },
          { label: "Roles del sistema", value: summary.systemRoles },
          { label: "Roles personalizados", value: summary.customRoles },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Paper className={styles.summaryCard} elevation={0}>
              <Typography className={styles.summaryLabel}>
                {item.label}
              </Typography>
              <Typography className={styles.summaryValue}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper className={styles.helperCard} elevation={0}>
        <Typography className={styles.roleName}>
          Administra qué puede hacer cada perfil interno del proveedor.
        </Typography>
        <Typography className={styles.secondaryText}>
          Los roles del sistema pueden ajustarse en permisos y descripción, pero
          no se eliminan. Los roles personalizados sí se pueden crear y remover.
        </Typography>
      </Paper>

      <Box display="flex" justifyContent="flex-end" marginBottom={3}>
        <CustomButton
          color="primary"
          shape="little-rounded"
          icon={AddCircleOutlineIcon}
          onClick={openCreateDialog}
        >
          Crear rol
        </CustomButton>
      </Box>

      <MaterialTable
        title=""
        columns={columns}
        data={roles}
        isLoading={isLoadingRoles || isLoadingPermissions}
        localization={materialTableES}
        options={{
          search: true,
          filtering: false,
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
          },
          rowStyle: {
            fontSize: "14px",
            color: "#1C252E",
          },
          searchFieldVariant: "outlined",
        }}
      />

      <Dialog
        open={isRoleDialogOpen}
        onClose={closeRoleDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedRole ? `Editar rol: ${selectedRole.name}` : "Crear rol"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRole?.is_system ? (
            <Paper className={styles.helperCard} elevation={0}>
              <Typography className={styles.roleName}>
                Rol del sistema
              </Typography>
              <Typography className={styles.secondaryText}>
                Puedes actualizar la descripción y los permisos, pero el nombre
                está reservado por la plataforma.
              </Typography>
            </Paper>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre del rol"
                value={roleForm.name}
                disabled={selectedRole?.is_system}
                onChange={(event) =>
                  setRoleForm((prev) => ({ ...prev, name: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                label="Descripción"
                value={roleForm.description}
                onChange={(event) =>
                  setRoleForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>

          <Box marginTop={3} marginBottom={2}>
            <Typography className={styles.roleName}>Permisos</Typography>
            <Typography className={styles.secondaryText}>
              Marca los permisos que va a heredar este rol.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {Object.entries(permissionsByModule).map(
              ([module, modulePermissions]) => (
                <Grid item xs={12} md={6} key={module}>
                  <Paper className={styles.permissionCard} elevation={0}>
                    <Typography className={styles.moduleTitle}>
                      {module}
                    </Typography>
                    {modulePermissions.map((permission) => (
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            color="primary"
                            checked={roleForm.permission_ids.includes(
                              permission.id
                            )}
                            onChange={() => togglePermission(permission.id)}
                          />
                        }
                        label={
                          <div>
                            <Typography className={styles.roleName}>
                              {permission.action}
                            </Typography>
                            <Typography className={styles.secondaryText}>
                              {permission.description}
                            </Typography>
                          </div>
                        }
                      />
                    ))}
                  </Paper>
                </Grid>
              )
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="outlined"
            shape="little-rounded"
            onClick={closeRoleDialog}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            shape="little-rounded"
            onClick={handleSaveRole}
            isLoading={isCreatingRole || isUpdatingRole || isSavingPermissions}
          >
            Guardar rol
          </CustomButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default RolesManagement;
